import React, { useEffect, useMemo, useState } from 'react';
import { onboardingApi } from '../services/api';
import { formatApiError } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  UserPlus, Link as LinkIcon, Copy, Check, ArrowsClockwise, X,
  CheckCircle, XCircle, WarningCircle, Clock, EnvelopeSimple, IdentificationCard, Car, Trash, ArrowRight, Buildings,
} from '@phosphor-icons/react';

const STATUS_LABELS = {
  pending: 'Ausstehend',
  submitted: 'Eingereicht',
  approved: 'Übernommen',
  rejected: 'Abgelehnt',
};
const STATUS_STYLES = {
  pending: 'bg-[#F7F8FA] text-[#525866] border-[#E5E7EB]',
  submitted: 'bg-[#FFF7E6] text-[#B8860B] border-[#F4D48A]',
  approved: 'bg-[#E6F7EB] text-[#009951] border-[#B7E4C7]',
  rejected: 'bg-[#FFF5F5] text-[#E60000] border-[#FFD4D4]',
};
const STATUS_ICONS = {
  pending: Clock,
  submitted: WarningCircle,
  approved: CheckCircle,
  rejected: XCircle,
};

const DOC_LABELS = {
  id_front: 'Personalausweis · Vorderseite',
  id_back: 'Personalausweis · Rückseite',
  dl_front: 'Führerschein · Vorderseite',
  dl_back: 'Führerschein · Rückseite',
};

const CustomerOnboardings = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showInviteResult, setShowInviteResult] = useState(null);
  const [inviteForm, setInviteForm] = useState({ invited_email: '', invited_name: '', expires_days: 7 });
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [copyState, setCopyState] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await onboardingApi.list(params);
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const grouped = useMemo(() => {
    const g = { pending: [], submitted: [], approved: [], rejected: [] };
    for (const it of items) g[it.status]?.push(it);
    return g;
  }, [items]);

  const openDetail = async (item) => {
    try {
      const res = await onboardingApi.get(item.id);
      setSelected(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      const res = await onboardingApi.createInvitation({
        invited_email: inviteForm.invited_email.trim() || null,
        invited_name: inviteForm.invited_name.trim() || null,
        expires_days: parseInt(inviteForm.expires_days) || 7,
      });
      toast.success('Einladung erstellt');
      setShowInvite(false);
      setInviteForm({ invited_email: '', invited_name: '', expires_days: 7 });
      // Build the full URL from window.location.origin because backend returns relative if no origin header
      const url = res.data.public_url?.startsWith('http')
        ? res.data.public_url
        : `${window.location.origin}${res.data.public_url}`;
      setShowInviteResult({ ...res.data, public_url: url });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreatingInvite(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState((s) => ({ ...s, [key]: true }));
      setTimeout(() => setCopyState((s) => ({ ...s, [key]: false })), 1500);
    } catch (e) {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const approve = async (merge = false) => {
    if (!selected) return;
    setProcessing(true);
    try {
      const payload = merge && selected.duplicate_customer
        ? { merge_into_customer_id: selected.duplicate_customer.id }
        : {};
      const res = await onboardingApi.approve(selected.id, payload);
      toast.success(res.data.message || 'Übernommen');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessing(false);
    }
  };

  const reject = async () => {
    if (!selected) return;
    const reason = window.prompt('Grund für die Ablehnung (optional):') || '';
    setProcessing(true);
    try {
      await onboardingApi.reject(selected.id, { reason });
      toast.success('Kunden-Anfrage abgelehnt');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessing(false);
    }
  };

  const removeItem = async () => {
    if (!selected) return;
    if (!window.confirm('Diese Kunden-Anfrage wirklich löschen? Die hochgeladenen Dokumente bleiben archiviert.')) return;
    setProcessing(true);
    try {
      await onboardingApi.remove(selected.id);
      toast.success('Anfrage gelöscht');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessing(false);
    }
  };

  const publicRegisterUrl = `${window.location.origin}/registrieren`;

  return (
    <div className="space-y-6" data-testid="customer-onboardings-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#111418]">Kunden-Anfragen</h1>
          <p className="text-sm text-[#525866] mt-1">
            Selbst-Registrierungen mit Ausweis- und Führerscheinfotos. Prüfen und in die Kundenkartei übernehmen.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => copyToClipboard(publicRegisterUrl, 'public_link')}
            data-testid="copy-public-registration-link"
          >
            {copyState.public_link ? <Check size={14} weight="bold" className="mr-1.5" /> : <LinkIcon size={14} weight="bold" className="mr-1.5" />}
            {copyState.public_link ? 'Kopiert!' : 'Öffentlichen Link kopieren'}
          </Button>
          <Button
            className="btn-primary"
            onClick={() => setShowInvite(true)}
            data-testid="create-invitation-btn"
          >
            <UserPlus size={14} weight="bold" className="mr-1.5" />
            Einladung erstellen
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-xs uppercase text-[#8A94A6]">Status:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]" data-testid="onboarding-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="pending">Ausstehend</SelectItem>
            <SelectItem value="submitted">Eingereicht (Prüfung)</SelectItem>
            <SelectItem value="approved">Übernommen</SelectItem>
            <SelectItem value="rejected">Abgelehnt</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-btn">
          <ArrowsClockwise size={14} weight="bold" className="mr-1" /> Aktualisieren
        </Button>
      </div>

      {/* Inbox Kacheln */}
      <div className="space-y-6">
        {['submitted', 'pending', 'approved', 'rejected'].map((st) => {
          const list = statusFilter === 'all' ? grouped[st] : (statusFilter === st ? items : []);
          if (!list || list.length === 0) return null;
          const Icon = STATUS_ICONS[st];
          return (
            <div key={st}>
              <h2 className="text-sm font-semibold text-[#525866] mb-2 flex items-center gap-2">
                <Icon size={14} weight="bold" />
                {STATUS_LABELS[st]} <span className="text-[#8A94A6]">({list.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {list.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => openDetail(it)}
                    className="card-swiss p-4 text-left hover:border-[#E60000] transition-colors"
                    data-testid={`onboarding-card-${it.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-bold text-[#111418]">
                          {(it.form?.first_name || it.invited_name?.split(' ')[0] || '—')}{' '}
                          {(it.form?.last_name || (it.invited_name?.includes(' ') ? it.invited_name.split(' ').slice(1).join(' ') : ''))}
                        </p>
                        <p className="text-xs text-[#525866] mt-0.5 truncate">
                          {it.form?.email || it.invited_email || 'ohne E-Mail'}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-sm border ${STATUS_STYLES[it.status]}`}>
                        {STATUS_LABELS[it.status]}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-0.5 bg-[#F7F8FA] text-[#525866] rounded-sm">
                        {it.source === 'invitation' ? 'Einladung' : 'Öffentlich'}
                      </span>
                      {it.form?.customer_type === 'business' && (
                        <span className="px-2 py-0.5 bg-[#FFF7E6] text-[#B8860B] rounded-sm flex items-center gap-1">
                          <Buildings size={12} weight="bold" /> Firma
                        </span>
                      )}
                      {it.duplicate_customer && (
                        <span className="px-2 py-0.5 bg-[#FFF7E6] text-[#B8860B] rounded-sm flex items-center gap-1">
                          <WarningCircle size={12} weight="bold" /> Duplikat (E-Mail)
                        </span>
                      )}
                      {Object.keys(it.documents || {}).length > 0 && (
                        <span className="px-2 py-0.5 bg-[#F7F8FA] text-[#525866] rounded-sm">
                          {Object.keys(it.documents).length}/4 Fotos
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#8A94A6] mt-2">
                      {it.submitted_at
                        ? `Eingereicht: ${new Date(it.submitted_at).toLocaleString('de-DE')}`
                        : `Erstellt: ${new Date(it.created_at).toLocaleString('de-DE')}`}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {!loading && items.length === 0 && (
          <div className="card-swiss p-12 text-center" data-testid="empty-state">
            <IdentificationCard size={40} weight="thin" className="mx-auto text-[#C8CBD0]" />
            <p className="text-[#525866] mt-3">Noch keine Kunden-Anfragen. Erstelle eine Einladung oder teile den öffentlichen Registrierungs-Link.</p>
          </div>
        )}
      </div>

      {/* Einladung erstellen */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Neue Einladung erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline">E-Mail (optional)</Label>
              <Input
                type="email"
                value={inviteForm.invited_email}
                onChange={(e) => setInviteForm({ ...inviteForm, invited_email: e.target.value })}
                placeholder="kunde@beispiel.de"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label className="overline">Name (optional)</Label>
              <Input
                value={inviteForm.invited_name}
                onChange={(e) => setInviteForm({ ...inviteForm, invited_name: e.target.value })}
                placeholder="z.B. Max Mustermann"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label className="overline">Gültig für (Tage)</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={inviteForm.expires_days}
                onChange={(e) => setInviteForm({ ...inviteForm, expires_days: e.target.value })}
                data-testid="invite-expires-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowInvite(false)} disabled={creatingInvite}>Abbrechen</Button>
              <Button
                className="btn-primary"
                onClick={createInvite}
                disabled={creatingInvite}
                data-testid="invite-submit-btn"
              >
                {creatingInvite ? 'Wird erstellt…' : 'Einladung erstellen'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ergebnis der Einladung (Link + Kopieren) */}
      <Dialog open={!!showInviteResult} onOpenChange={(o) => !o && setShowInviteResult(null)}>
        <DialogContent className="max-w-lg" data-testid="invite-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#009951]">
              <CheckCircle size={20} weight="bold" />
              Einladungs-Link erstellt
            </DialogTitle>
          </DialogHeader>
          {showInviteResult && (
            <div className="space-y-3">
              <p className="text-sm text-[#525866]">
                Kopiere den Link und sende ihn per E-Mail, WhatsApp oder SMS an den Kunden.
                Der Link ist bis <strong>{new Date(showInviteResult.expires_at).toLocaleString('de-DE')}</strong> gültig.
              </p>
              <div className="p-3 bg-[#F7F8FA] rounded-sm border border-[#E5E7EB] break-all font-mono text-xs" data-testid="invite-result-url">
                {showInviteResult.public_url}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => copyToClipboard(showInviteResult.public_url, 'inv_res')}
                  data-testid="invite-result-copy-btn"
                >
                  {copyState.inv_res ? <Check size={14} weight="bold" className="mr-1.5" /> : <Copy size={14} weight="bold" className="mr-1.5" />}
                  {copyState.inv_res ? 'Kopiert!' : 'Link kopieren'}
                </Button>
                {showInviteResult.invited_email && (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      const subject = encodeURIComponent('Ihre Kundenregistrierung');
                      const body = encodeURIComponent(
                        `Sehr geehrte/r ${showInviteResult.invited_name || 'Kunde'},\n\n` +
                        `bitte tragen Sie unter folgendem Link Ihre Daten ein und laden Sie Ausweis + Führerschein hoch:\n\n${showInviteResult.public_url}\n\n` +
                        `Der Link ist ${Math.round((new Date(showInviteResult.expires_at) - new Date()) / 86400000)} Tage gültig.\n\nVielen Dank!`
                      );
                      window.location.href = `mailto:${showInviteResult.invited_email}?subject=${subject}&body=${body}`;
                    }}
                    data-testid="invite-result-mailto-btn"
                  >
                    <EnvelopeSimple size={14} weight="bold" className="mr-1.5" />
                    Per E-Mail senden
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail-Ansicht */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="onboarding-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-8">
                  <span>Kunden-Anfrage</span>
                  <span className={`text-xs px-2 py-0.5 rounded-sm border ${STATUS_STYLES[selected.status]}`}>
                    {STATUS_LABELS[selected.status]}
                  </span>
                </DialogTitle>
              </DialogHeader>

              {selected.duplicate_customer && selected.status !== 'approved' && (
                <div className="p-3 bg-[#FFF7E6] border border-[#F4D48A] rounded-sm text-sm" data-testid="duplicate-warning">
                  <p className="font-semibold text-[#B8860B] flex items-center gap-2">
                    <WarningCircle size={14} weight="bold" /> Duplikat gefunden
                  </p>
                  <p className="text-[#111418] mt-1">
                    Ein Kunde mit dieser E-Mail existiert bereits:{' '}
                    <strong>{selected.duplicate_customer.first_name} {selected.duplicate_customer.last_name}</strong>{' '}
                    ({selected.duplicate_customer.email}).
                    Du kannst die neue Anfrage in diesen bestehenden Kunden mergen (Daten und Dokumente werden aufgefüllt).
                  </p>
                </div>
              )}

              {selected.form?.customer_type === 'business' && (
                <div className="p-3 bg-[#FFF7E6] border border-[#F4D48A] rounded-sm" data-testid="business-badge">
                  <p className="text-xs font-semibold text-[#B8860B] mb-2 flex items-center gap-1">
                    <Buildings size={12} weight="bold" /> Firma / Gewerbekunde
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    {[
                      ['Firmenname', selected.form?.company_name],
                      ['Rechtsform', selected.form?.company_legal_form],
                      ['Steuernummer', selected.form?.tax_number],
                      ['USt-IdNr.', selected.form?.vat_id],
                      ['Handelsregister', selected.form?.register_number],
                      ['Registergericht', selected.form?.register_court],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <p className="text-xs text-[#8A94A6]">{k}</p>
                        <p className="text-[#111418] font-medium">{v || '—'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {[
                  ['Kundentyp', selected.form?.customer_type === 'business' ? 'Firma' : 'Privat'],
                  ['Anrede', selected.form?.salutation],
                  ['Vorname', selected.form?.first_name],
                  ['Nachname', selected.form?.last_name],
                  ['Geburtsdatum', selected.form?.date_of_birth],
                  ['Geburtsort', selected.form?.birth_place],
                  ['E-Mail', selected.form?.email],
                  ['Telefon', selected.form?.phone],
                  ['Straße', selected.form?.address],
                  ['PLZ', selected.form?.postal_code],
                  ['Ort', selected.form?.city],
                  ['Land', selected.form?.country],
                  ['Ausweisnummer', selected.form?.id_card_number],
                  ['Ausweis gültig bis', selected.form?.id_card_expiry],
                  ['Führerscheinnummer', selected.form?.driver_license_number],
                  ['Führerschein gültig bis', selected.form?.driver_license_expiry],
                  ['Ausstellende Behörde', selected.form?.driver_license_authority],
                  ['Klassen', (selected.form?.driver_license_classes || []).join(', ')],
                ].map(([k, v]) => (
                  <div key={k} className="border-b border-[#F0F1F3] pb-1">
                    <p className="text-xs text-[#8A94A6]">{k}</p>
                    <p className="text-[#111418] font-medium">{v || '—'}</p>
                  </div>
                ))}
              </div>
              {selected.form?.notes && (
                <div className="p-3 bg-[#F7F8FA] rounded-sm text-sm">
                  <p className="text-xs text-[#8A94A6] mb-1">Anmerkungen</p>
                  <p className="text-[#111418]">{selected.form.notes}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-semibold text-[#111418] mb-2">Dokumente</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['id_front', 'id_back', 'dl_front', 'dl_back'].map((k) => {
                    const has = !!(selected.documents || {})[k];
                    return (
                      <div key={k} className="border border-[#E5E7EB] rounded-sm overflow-hidden" data-testid={`doc-tile-${k}`}>
                        {has ? (
                          <a
                            href={onboardingApi.adminDocUrl(selected.id, k)}
                            target="_blank"
                            rel="noreferrer"
                            className="block"
                          >
                            <img
                              src={onboardingApi.adminDocUrl(selected.id, k)}
                              alt={DOC_LABELS[k]}
                              className="w-full h-32 object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.parentElement.innerHTML = '<div class="w-full h-32 flex items-center justify-center bg-[#F7F8FA] text-[#525866] text-xs">Vorschau nicht verfügbar</div>';
                              }}
                            />
                          </a>
                        ) : (
                          <div className="w-full h-32 flex items-center justify-center bg-[#F7F8FA] text-[#8A94A6] text-xs">
                            Fehlt
                          </div>
                        )}
                        <p className="text-xs text-center py-1.5 border-t border-[#E5E7EB] text-[#525866]">
                          {DOC_LABELS[k]}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="p-3 bg-[#F7F8FA] rounded-sm text-xs text-[#525866]">
                Quelle: <strong>{selected.source === 'invitation' ? 'Einladung' : 'Öffentliche Registrierung'}</strong>
                {selected.expires_at && <> · Läuft ab: {new Date(selected.expires_at).toLocaleString('de-DE')}</>}
                {selected.submitted_at && <> · Eingereicht: {new Date(selected.submitted_at).toLocaleString('de-DE')}</>}
                {selected.converted_customer_id && (
                  <> · Übernommen zu Kunde <a className="text-[#E60000] font-mono" href={`/customers/${selected.converted_customer_id}`}>{selected.converted_customer_id.substring(0, 8)}</a></>
                )}
              </div>

              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="ghost" onClick={() => setSelected(null)} disabled={processing}>
                  Schließen
                </Button>
                <Button
                  variant="outline"
                  onClick={removeItem}
                  disabled={processing}
                  className="text-[#525866]"
                  data-testid="onboarding-delete-btn"
                >
                  <Trash size={14} weight="bold" className="mr-1.5" /> Löschen
                </Button>
                {selected.status !== 'approved' && selected.status !== 'rejected' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={reject}
                      disabled={processing}
                      className="border-[#E60000] text-[#E60000] hover:bg-[#E60000]/10"
                      data-testid="onboarding-reject-btn"
                    >
                      <X size={14} weight="bold" className="mr-1.5" /> Ablehnen
                    </Button>
                    {selected.duplicate_customer ? (
                      <Button
                        onClick={() => approve(true)}
                        disabled={processing}
                        className="btn-primary"
                        data-testid="onboarding-merge-btn"
                      >
                        <ArrowRight size={14} weight="bold" className="mr-1.5" />
                        {processing ? 'Wird übernommen…' : 'In bestehenden Kunden mergen'}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => approve(false)}
                        disabled={processing || selected.status !== 'submitted'}
                        className="btn-primary"
                        data-testid="onboarding-approve-btn"
                      >
                        <Check size={14} weight="bold" className="mr-1.5" />
                        {processing ? 'Wird übernommen…' : 'Als neuen Kunden übernehmen'}
                      </Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerOnboardings;
